import React, { useEffect, useState } from 'react';
import axios from 'axios';
import type * as Yup from 'yup';

import Button from '~/components/core/Atomic/Buttons/Button';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import { AddIcon } from '~/components/deprecatedMuiIcons';
import DocumentTemplatesInfoBanner from '~/components/GenericTemplates/GenericTemplatesConfiguration/DocumentTemplatesInfoBanner/DocumentTemplatesInfoBanner';
import GenericTemplateBuilderContainer from '~/components/GenericTemplates/GenericTemplatesConfiguration/GenericTemplateBuilder/GenericTemplateBuilderContainer';
import GenericTemplateCardContainer from '~/components/GenericTemplates/GenericTemplatesConfiguration/GenericTemplateCard/GenericTemplateCardContainer';
import GenericTemplateAdvancedSearchContainerFormik from '~/components/GenericTemplates/GenericTemplatesConfiguration/GenericTemplateSearch/GenericTemplateAdvancedSearch/GenericTemplateAdvancedSearchContainerFormik';
import GenericTemplatesTable from '~/components/GenericTemplates/GenericTemplatesConfiguration/GenericTemplatesTable';
import type {
  AdvancedSearchFilterTypes,
  GenericTemplatesRoutes,
  PaginationProps,
  TemplateFormSubmitValuesProps,
  TemplateMetadata,
  TemplatesApiResponse,
  TemplateTypeKey,
} from '~/components/GenericTemplates/types';
import { DEFAULT_TEMPLATES_PAGINATION_PROPS } from '~/components/GenericTemplates/utils/genericTemplatesUtils';
import { GENERIC_TEMPLATES_ROUTES } from '~/components/GenericTemplates/utils/routes';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import useDataFetcher from '~/components/useDataFetcher';
import { getAxiosParamsSerializer, reportAxiosError } from '~/Utils';
import cn from '~/Utils/cn';

import DocumentsTemplatesActions from './DocumentTemplateActions/DocumentTemplateActions';
import DocumentTemplateCardContainer from './GenericTemplateCard/DocumentTemplateCardContainer';

interface TemplateFormValidationSchema {
  [key: string]: Yup.SchemaOf<unknown>;
}

const FETCH_PARAMS_SERIALIZER = getAxiosParamsSerializer('none');

export interface GenericTemplatesAdditionalPropsType<
  TProps extends object,
  TFormValues extends TemplateFormSubmitValuesProps = TemplateFormSubmitValuesProps,
> {
  component: React.FC<TProps>;
  props: TProps;
  defaultFormValues?: TFormValues;
  validationSchemaExtension?: TemplateFormValidationSchema;
}

export interface GenericTemplatesTableContainerPropsType<
  TProps extends object = object,
  TFormValues extends TemplateFormSubmitValuesProps = TemplateFormSubmitValuesProps,
> {
  templateType: TemplateTypeKey;
  shouldDisplayType: boolean;
  isCreateTemplateEnabled: boolean;
  templateRoutesDict?: GenericTemplatesRoutes;
  additionalProps?: GenericTemplatesAdditionalPropsType<TProps, TFormValues>;
}

const GenericTemplatesTableContainer = <
  TProps extends object = object,
  TFormValues extends TemplateFormSubmitValuesProps = TemplateFormSubmitValuesProps,
>({
  templateType,
  shouldDisplayType,
  isCreateTemplateEnabled,
  templateRoutesDict = GENERIC_TEMPLATES_ROUTES,
  additionalProps,
}: GenericTemplatesTableContainerPropsType<TProps, TFormValues>) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organization } = useSysconfig();

  const [paginationProps, setPaginationProps] = useState(DEFAULT_TEMPLATES_PAGINATION_PROPS);
  const [templates, setTemplates] = useState<TemplateMetadata[]>([]);
  const [templatesCount, setTemplatesCount] = useState(0);
  const [searchParams, setSearchParams] = useState({
    template_type: templateType,
    template_name: '',
  });
  const [isCreateTemplateDialogOpen, setIsCreateTemplateDialogOpen] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(0);

  const { data, isLoading, isError, reloadData } = useDataFetcher<TemplatesApiResponse>(
    templateRoutesDict.GET_TEMPLATES(organization.id),
    {
      params: {
        ...paginationProps,
        ...searchParams,
        page: paginationProps.page + 1,
      },
      paramsSerializer: FETCH_PARAMS_SERIALIZER,
    }
  );

  const displayLoading = isLoading || isError;

  useEffect(() => {
    setTemplates(data?.templates_metadata ?? []);
    setTemplatesCount(data?.count ?? 0);
  }, [data]);

  const isDocumentTemplate = templateType === 'document';

  const handleSelectTemplate = (template_id: number) => {
    setSelectedTemplateId(template_id);
  };

  const handleUpdatePaginationProps = (updatedPaginationProps: PaginationProps) => {
    setPaginationProps(updatedPaginationProps);
  };

  const handleAdvancedTemplatesSearch = (values: AdvancedSearchFilterTypes) => {
    setPaginationProps((prevPaginationProps) => ({ ...prevPaginationProps, page: 0 }));
    setSearchParams({
      ...searchParams,
      ...values,
      template_type: templateType,
    });
  };

  const handleOpenCreateTemplateDialog = () => {
    setIsCreateTemplateDialogOpen(true);
  };

  const handleCloseCreateTemplateDialog = () => {
    setIsCreateTemplateDialogOpen(false);
  };

  const handleSubmitTemplate = async (values: TemplateFormSubmitValuesProps) => {
    try {
      await axios.post(templateRoutesDict.CREATE_TEMPLATE(organization.id), values);
      await reloadData();
    } catch (error) {
      await reportAxiosError(error);
      throw error;
    }
  };

  // Wrap reloadData to always return void for reloadTable and similar props
  const reloadDataVoid = async () => {
    await reloadData();
  };

  return (
    <div>
      <div className="m-12 grid grid-cols-2 justify-between p-12">
        {/* TODO: NGTPA-16665 - Add Import+Export templates */}
        <div />
        {isCreateTemplateEnabled ? (
          <div className="flex items-center justify-end">
            <Button color="primary" disabled={false} onClick={handleOpenCreateTemplateDialog}>
              <AddIcon />
              Add template
            </Button>
          </div>
        ) : null}
        {isDocumentTemplate ? (
          <div className="flex items-center justify-end">
            <DocumentsTemplatesActions
              organizationId={organization.id}
              reloadDataDocumentTemplates={reloadData}
              documentTemplates={templates}
            />
          </div>
        ) : null}
      </div>
      <GenericTemplateAdvancedSearchContainerFormik
        handleSubmit={handleAdvancedTemplatesSearch}
        isLoading={isLoading}
      />
      {isDocumentTemplate ? <DocumentTemplatesInfoBanner /> : null}
      <div className={cn('grid gap-4', selectedTemplateId ? 'grid-cols-12' : 'grid-cols-1')}>
        <div className="col-span-8">
          {displayLoading ? (
            <SkeletonTable rowsCount={10} columnsCount={10} isError={isError} />
          ) : (
            <GenericTemplatesTable
              templates={templates}
              templatesCount={templatesCount}
              onSelectTemplate={handleSelectTemplate}
              onUpdatePaginationProps={handleUpdatePaginationProps}
              paginationProps={paginationProps}
              shouldDisplayType={shouldDisplayType}
              selectedTemplateId={selectedTemplateId}
              reloadTable={reloadDataVoid}
              templateRoutesDict={templateRoutesDict}
            />
          )}
        </div>
        {selectedTemplateId ? (
          !isDocumentTemplate ? (
            <div className="col-span-4">
              <GenericTemplateCardContainer
                templateId={selectedTemplateId}
                reloadTable={reloadDataVoid}
                additionalProps={additionalProps}
              />
            </div>
          ) : (
            <div className="col-span-4">
              <DocumentTemplateCardContainer
                templateId={selectedTemplateId}
                reloadTable={reloadDataVoid}
                templatesMetadata={templates}
              />
            </div>
          )
        ) : null}
        {isCreateTemplateDialogOpen ? (
          <GenericTemplateBuilderContainer
            templateType={templateType}
            handleClose={handleCloseCreateTemplateDialog}
            handleSubmit={handleSubmitTemplate}
            additionalProps={additionalProps}
          />
        ) : null}
      </div>
    </div>
  );
};

export default GenericTemplatesTableContainer;
